/**
 * Ring progression (ADR-64).
 *
 * The ring declares exactly two things about order: the current lot
 * (SET_CURRENT_LOT, spec 4.1) and, since ADR-64, the previous disposition
 * (RingState.previousLot). "Next" is a ring fact only while the current lot is
 * UPCOMING - the clerk has staged it and nothing else. At every other moment
 * "next" is the SCHEDULE: the catalog's run order after the current or
 * last-disposed lot, skipping lots that will not run.
 *
 * Before this module the clerk console, Auction TV, Auction Progress and the
 * GFX composition each had their own version of that rule, and they did not
 * agree (one skipped nothing, one skipped notices only, one skipped sold and
 * scratched only). Two screens at one sale could name different horses as
 * next. The rule lives here once, pure, and every consumer calls it.
 *
 * Never "the next hip number": hips carry inserts, A/B splits and gaps, and
 * a scratched horse keeps its number while never setting foot in the ring.
 */

/** The catalog facts progression reads. Every consumer's lot shape has them. */
export interface RunOrderLot {
  readonly id: string;
  readonly hipNumber: string;
  readonly runOrder: number | null;
  /** Catalog status (`draft`, `approved`, `scratched`, `sold`, `no_sale`,
   *  `passed`). Absent means unknown, which is treated as sellable. */
  readonly status?: string;
  /** Lot kind (ADR-27). `notice` lots occupy a slot and never open. */
  readonly kind?: string;
}

/** Statuses of a lot that is behind the sale, not ahead of it. */
export const DISPOSED_STATUSES: ReadonlySet<string> = new Set(['sold', 'no_sale', 'passed']);

/**
 * A lot the ring can still sell.
 *
 * Scratched horses never run. Already-disposed ones are behind the sale. A
 * `notice` lot is a catalog slot, not a horse. Everything else is fair game,
 * INCLUDING `draft` - a horse added at the ring is created as a draft and has
 * to be sellable within seconds of being entered.
 */
export function sellable(lot: { readonly status?: string; readonly kind?: string }): boolean {
  if (lot.kind === 'notice') return false;
  if (lot.status === undefined) return true;
  return lot.status !== 'scratched' && !DISPOSED_STATUSES.has(lot.status);
}

/** The catalog in run order: `runOrder` first (unset last), then the hip
 *  compared numerically so 2 comes before 10 and 24A after 24. */
export function runOrdered<T extends { readonly hipNumber: string; readonly runOrder: number | null }>(lots: readonly T[]): T[] {
  return [...lots].sort(
    (a, b) => (a.runOrder ?? 1e9) - (b.runOrder ?? 1e9) || a.hipNumber.localeCompare(b.hipNumber, undefined, { numeric: true }),
  );
}

/**
 * The next horse to run after `afterLotId`, per the run order. `run` must
 * already be in run order. Returns null at the end of the sale, or when the
 * anchor lot is not in the run at all (nothing is guessed).
 */
export function nextInRunOrder<T extends { readonly id: string; readonly status?: string; readonly kind?: string }>(
  run: readonly T[],
  afterLotId: string,
): T | null {
  const i = run.findIndex((l) => l.id === afterLotId);
  if (i < 0) return null;
  return run.slice(i + 1).find(sellable) ?? null;
}

/** The ring facts progression reads: the public snapshot has all three. */
export interface ProgressionRingFacts {
  readonly lotId: string | null;
  readonly lotState: string;
  readonly previousLot?: { readonly lotId: string } | null;
}

export interface RingProgression<T> {
  /** The lot on the ring now (the snapshot's lot), if the catalog knows it. */
  readonly current: T | null;
  /** The ring's last disposition, if the catalog knows it. */
  readonly previous: T | null;
  /**
   * What runs next. `RING` when the ring itself has declared it (the current
   * lot is staged UPCOMING); `SCHEDULE` when it is the run order's answer
   * after the current or last-disposed lot. null when nothing is coming or
   * nothing can be anchored.
   */
  readonly next: { readonly lot: T; readonly source: 'RING' | 'SCHEDULE' } | null;
  /** The lots still to run after `next`, in run order (the schedule). */
  readonly upcoming: readonly T[];
}

/**
 * Previous, current and next, from the ring's facts and the catalog. Pure.
 * Consumers render this; none of them re-derives it.
 */
export function ringProgression<T extends RunOrderLot>(ring: ProgressionRingFacts, lots: readonly T[]): RingProgression<T> {
  const run = runOrdered(lots);
  const byId = (id: string | null | undefined): T | null => (id ? run.find((l) => l.id === id) ?? null : null);
  const current = byId(ring.lotId);
  const previous = byId(ring.previousLot?.lotId);

  if (current && ring.lotState === 'UPCOMING') {
    const anchor = current.id;
    return { current, previous, next: { lot: current, source: 'RING' }, upcoming: run.slice(run.findIndex((l) => l.id === anchor) + 1).filter(sellable) };
  }
  const anchorId = ring.lotId ?? ring.previousLot?.lotId ?? null;
  if (!anchorId) return { current, previous, next: null, upcoming: run.filter(sellable) };
  const i = run.findIndex((l) => l.id === anchorId);
  if (i < 0) return { current, previous, next: null, upcoming: [] };
  const rest = run.slice(i + 1).filter(sellable);
  const next = rest[0];
  return { current, previous, next: next ? { lot: next, source: 'SCHEDULE' } : null, upcoming: rest.slice(1) };
}
